import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { AiDifyCapabilityFeatures, PatternFeatureKey } from '../types/aiDifyCapability';
import { PATTERN_FEATURE_MAP } from '../types/aiDifyCapability';
import type { SkillOption } from '../types/skillAsset';

/** 特性显示配置 */
interface FeatureDisplayItem {
  readonly key: keyof AiDifyCapabilityFeatures;
  readonly label: string;
  readonly icon: string;
}

const FEATURE_DISPLAY_LIST: readonly FeatureDisplayItem[] = [
  { key: 'simple_mode', label: '快速模式', icon: 'fa-regular fa-lightbulb' },
  { key: 'enhanced_mode', label: '增强模式', icon: 'fa-solid fa-bolt' },
  { key: 'site_search', label: '站内搜索', icon: 'fa-solid fa-magnifying-glass' },
  { key: 'skills', label: '技能', icon: 'fa-solid fa-puzzle-piece' },
];

/** 判断是否为 pattern 类型的特性键 */
const isPatternKey = (key: string): key is PatternFeatureKey =>
  key in PATTERN_FEATURE_MAP;

export interface FeatureBarProps {
  /** 当前能力的 features 配置 */
  readonly features?: AiDifyCapabilityFeatures;
  /** 技能选项列表（仅 skills=true 时有效） */
  readonly skillOptions?: readonly SkillOption[];
  /** 技能选项加载中 */
  readonly skillsLoading?: boolean;
  /** 已选中的技能 ID 列表 */
  readonly selectedSkillIds?: readonly string[];
  /** 切换某个技能的选中状态 */
  readonly onToggleSkill?: (id: string) => void;
  /** 当前选中的模式（pattern）特性键，null 表示未选 */
  readonly selectedPattern?: PatternFeatureKey | null;
  /** 切换模式选中状态 */
  readonly onSelectPattern?: (pattern: PatternFeatureKey | null) => void;
}

export const FeatureBar: React.FC<FeatureBarProps> = ({
  features,
  skillOptions = [],
  skillsLoading = false,
  selectedSkillIds = [],
  onToggleSkill,
  selectedPattern = null,
  onSelectPattern,
}) => {
  const [skillDropdownOpen, setSkillDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /** 有效的（已启用的）特性列表 */
  const enabledFeatures: readonly FeatureDisplayItem[] = useMemo(() => {
    if (!features) return [];
    return FEATURE_DISPLAY_LIST.filter((item) => features[item.key]);
  }, [features]);

  /** 切换 skills 下拉框 */
  const handleSkillBtnClick = useCallback((): void => {
    setSkillDropdownOpen((prev) => !prev);
  }, []);

  /** 选中/取消某个技能 */
  const handleSkillOptionClick = useCallback(
    (id: string): void => {
      onToggleSkill?.(id);
    },
    [onToggleSkill]
  );

  /** 点击模式标签：单选切换（再次点击取消） */
  const handlePatternClick = useCallback(
    (key: PatternFeatureKey): void => {
      if (!onSelectPattern) return;
      onSelectPattern(selectedPattern === key ? null : key);
    },
    [onSelectPattern, selectedPattern]
  );

  /** 点击外部关闭下拉 */
  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>): void => {
    // 如果焦点移出了整个下拉容器，则关闭
    if (dropdownRef.current && !dropdownRef.current.contains(e.relatedTarget as Node)) {
      setSkillDropdownOpen(false);
    }
  }, []);

  // 没有启用任何特性时不渲染
  if (enabledFeatures.length === 0) {
    return null;
  }

  /** 当前选中技能数量 */
  const selectedCount = selectedSkillIds.length;

  return (
    <div className="feature-bar">
      {enabledFeatures.map((item) => {
        // skills 特殊处理：带下拉
        if (item.key === 'skills') {
          return (
            <div
              key={item.key}
              className="feature-bar-dropdown-wrapper"
              ref={dropdownRef}
              onBlur={handleBlur}
            >
              <button
                type="button"
                className={`feature-bar-pill${skillDropdownOpen ? ' active' : ''}${selectedCount > 0 ? ' has-selection' : ''}`}
                onClick={handleSkillBtnClick}
              >
                <i className={item.icon} />
                <span>{item.label}</span>
                {selectedCount > 0 && (
                  <span className="feature-bar-pill-badge">{selectedCount}</span>
                )}
                <i className={`fa-solid fa-chevron-${skillDropdownOpen ? 'up' : 'down'} feature-bar-arrow`} />
              </button>

              {skillDropdownOpen && (
                <div className="feature-bar-dropdown">
                  {skillsLoading && (
                    <div className="feature-bar-dropdown-loading">加载中...</div>
                  )}
                  {!skillsLoading && skillOptions.length === 0 && (
                    <div className="feature-bar-dropdown-empty">暂无可用技能</div>
                  )}
                  {!skillsLoading &&
                    skillOptions.map((opt) => {
                      const isSelected = selectedSkillIds.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          className={`feature-bar-dropdown-item${isSelected ? ' selected' : ''}`}
                          onClick={(): void => handleSkillOptionClick(opt.id)}
                        >
                          <span className="feature-bar-dropdown-item-check">
                            {isSelected && <i className="fa-solid fa-check" />}
                          </span>
                          <span className="feature-bar-dropdown-item-name">{opt.name}</span>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          );
        }

        // ===== 模式标签：可点击单选 =====
        if (isPatternKey(item.key)) {
          const patternKey: PatternFeatureKey = item.key;
          const isSelected = selectedPattern === patternKey;
          return (
            <button
              key={patternKey}
              type="button"
              className={`feature-bar-pill selectable${isSelected ? ' selected' : ''}`}
              onClick={(): void => handlePatternClick(patternKey)}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
              {isSelected && (
                <i className="fa-solid fa-check feature-bar-check-icon" />
              )}
            </button>
          );
        }

        // 其他：纯展示标签（兜底，目前不会走到这里）
        return (
          <span key={item.key} className="feature-bar-pill static">
            <i className={item.icon} />
            <span>{item.label}</span>
          </span>
        );
      })}
    </div>
  );
};